"use client";

import Grid from "@mui/material/Grid2";
import React, { useEffect, useLayoutEffect, useRef } from "react";

import MDBox from "@/components/atoms/MDBox";
import ChatBalloon from "@/components/molecules/ChatBalloon";
import { useGroupedMessages } from "@/hooks/requests/message/use-group-messages";
import { useChatScroll } from "@/hooks/use-chat-scroll";
import { IChat } from "@/interfaces/chat.interfaces";
import { IMessage } from "@/interfaces/message.interface";
import { CurrentChatMessages } from "@/messages/chat/current-chat.messages";

import DateCard from "./dateCard";

type CurrentChatContentProps = {
  chatMessages: IMessage[];
  messages: CurrentChatMessages;
  selectedChat: IChat | null;
  lastPageLoadTime: Date | null;
};

export default function CurrentChatContent({
  chatMessages,
  messages,
  selectedChat,
  lastPageLoadTime,
}: CurrentChatContentProps) {
  const selectChatContactId = selectedChat ? selectedChat.contactId : 0;
  const { scrollToBottom, chatEndRef, containerRef } = useChatScroll();

  const { groupedMessages, sortedDates } = useGroupedMessages(chatMessages);

  const lastContactIdRef = useRef<number | null>(null);
  const lastMessagesLengthRef = useRef<number>(0);
  const firstMessageIdRef = useRef<number | string | null>(null);
  const prevScrollHeightRef = useRef<number>(0);
  const wasPrependRef = useRef<boolean>(false);

  const firstMessageId = chatMessages.length > 0 ? chatMessages[0].id : null;

  // Ao carregar mensagens antigas (prepend), mantém a posição de leitura atual
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const isPrepend =
      firstMessageIdRef.current !== null &&
      firstMessageId !== firstMessageIdRef.current &&
      chatMessages.length > lastMessagesLengthRef.current;

    wasPrependRef.current = isPrepend;

    if (isPrepend) {
      const delta = container.scrollHeight - prevScrollHeightRef.current;
      if (delta > 0) container.scrollTop = container.scrollTop + delta;
      lastMessagesLengthRef.current = chatMessages.length;
    }

    firstMessageIdRef.current = firstMessageId;
    prevScrollHeightRef.current = container.scrollHeight;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatMessages, firstMessageId]);

  // Scroll instantâneo ao entrar/trocar de conversa
  useEffect(() => {
    if (!selectedChat || chatMessages.length === 0) return;
    const raf1 = requestAnimationFrame(() => {
      requestAnimationFrame(() => scrollToBottom("auto"));
    });
    lastContactIdRef.current = selectChatContactId;
    lastMessagesLengthRef.current = chatMessages.length;
    return () => cancelAnimationFrame(raf1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectChatContactId, chatMessages.length > 0]);

  // Scroll suave apenas quando uma nova mensagem chega com a conversa já aberta
  useEffect(() => {
    if (!selectedChat || chatMessages.length === 0) return;

    const sameChat = lastContactIdRef.current === selectChatContactId;
    const grew = chatMessages.length > lastMessagesLengthRef.current;
    // Mensagens antigas entram no topo: não deve puxar o scroll para o final
    const isPrepend = wasPrependRef.current;
    wasPrependRef.current = false;

    if (sameChat && grew && !isPrepend) {
      const timeout = setTimeout(() => scrollToBottom("smooth"), 100);
      lastMessagesLengthRef.current = chatMessages.length;
      return () => clearTimeout(timeout);
    }

    if (isPrepend) return;

    // Sincroniza refs sem disparar scroll (troca de chat, paginação, etc.)
    lastContactIdRef.current = selectChatContactId;
    lastMessagesLengthRef.current = chatMessages.length;
  }, [chatMessages, scrollToBottom, selectedChat, selectChatContactId, lastPageLoadTime]);



  return (
    <MDBox
      ref={containerRef}
      display="flex"
      flex={1}
      flexDirection="column"
      overflow="auto"
      p={2}
      sx={{
        minHeight: 0,
        maxHeight: "100%",
        overscrollBehavior: "contain",
        WebkitOverflowScrolling: "touch",
      }}
    >
      {sortedDates.map((date) => [
        <Grid
          container
          justifyContent="center"
          key={date}
          size={12}
          sx={{ flexShrink: 0 }}
        >
          <DateCard>{date}</DateCard>
        </Grid>,
        ...groupedMessages[date].map((message, index) => (
          <ChatBalloon key={`${index}-${message.id}`} chatMessage={message} messages={messages} />
        )),
      ])}
      <div ref={chatEndRef} />
    </MDBox>
  );
}
